import { type HTMLAttributes, forwardRef } from 'react'
import clsx from 'clsx'

/**
 * Badge - Composant badge avec variants SERGE design system
 *
 * Badge stylisé pour afficher des états, statuts ou catégories.
 * Style terminal/cyber avec 5 variants sémantiques.
 *
 * @example
 * <Badge variant="success">Terminé</Badge>
 * <Badge variant="warning">En attente</Badge>
 */

export type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Variant du badge */
  variant?: BadgeVariant
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', children, ...props }, ref) => {
    const variantClasses = {
      default: 'bg-surface border-border text-foreground',
      success: 'bg-success-muted border-success text-success',
      warning: 'bg-warning-muted border-warning text-warning',
      error: 'bg-error-muted border-error text-error',
      info: 'bg-info-muted border-info text-info',
    }

    return (
      <span
        ref={ref}
        className={clsx(
          'inline-flex items-center gap-1.5 px-2.5 py-1',
          'border text-xs font-mono uppercase tracking-wider',
          'transition-all duration-200',
          variantClasses[variant],
          className
        )}
        {...props}
      >
        {children}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

export { Badge }
